import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bell, 
  Mail, 
  Shield, 
  Save,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const [settings, setSettings] = useState({
    email_notifications: true,
    notification_email: "",
    min_credit_score: 630
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success("Settings saved!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900">Settings</h1>
        <p className="text-sm md:text-base text-slate-500 mt-1">Configure your lead generation preferences</p>
      </div>

      <div className="space-y-6">
        {/* Notifications */}
        <Card className="border-slate-100" data-testid="notifications-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#A94442]" />
              Notifications
            </CardTitle>
            <CardDescription>
              Configure how you receive alerts for new leads
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-notifications" className="font-medium">Email Notifications</Label>
                <p className="text-sm text-slate-500">Receive email alerts for new leads</p>
              </div>
              <Switch
                id="email-notifications"
                checked={settings.email_notifications}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, email_notifications: checked }))}
                data-testid="email-notifications-switch"
              />
            </div>

            {settings.email_notifications && (
              <div>
                <Label htmlFor="notification-email">Notification Email</Label>
                <Input
                  id="notification-email"
                  type="email"
                  value={settings.notification_email || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, notification_email: e.target.value }))}
                  placeholder="your@email.com"
                  className="mt-1.5 h-11"
                  data-testid="notification-email-input"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Qualification Rules */}
        <Card className="border-slate-100" data-testid="qualification-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1A4D2E]" />
              Qualification Rules
            </CardTitle>
            <CardDescription>
              Set criteria for automatic lead qualification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="min-credit">Minimum Credit Score</Label>
              <div className="flex items-center gap-4 mt-1.5">
                <Input
                  id="min-credit"
                  type="number"
                  value={settings.min_credit_score}
                  onChange={(e) => setSettings(prev => ({ ...prev, min_credit_score: parseInt(e.target.value) || 630 }))}
                  className="h-11 w-32"
                  min={300}
                  max={850}
                  data-testid="min-credit-score-input"
                />
                <span className="text-slate-500">out of 850</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Leads with credit scores below this threshold will be automatically disqualified
              </p>
            </div>

            <div className="bg-[#FDB813]/10 rounded-xl p-4 flex gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">PPA Qualification Note</p>
                <p className="text-sm text-amber-700 mt-1">
                  Ion Solar PPA requires a minimum credit score of 630. Leads who make on-time payments 
                  may still qualify even with scores in the 630-699 range.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Capture Link */}
        <Card className="border-slate-100" data-testid="lead-capture-card">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-600" />
              Lead Capture Form
            </CardTitle>
            <CardDescription>
              Share this link to capture new leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={`${window.location.origin}/capture`}
                readOnly
                className="h-11 bg-slate-50"
                data-testid="capture-url-input"
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/capture`);
                  toast.success("Link copied!");
                }}
                className="bg-[#A94442] hover:bg-[#8B3836] px-6"
                data-testid="copy-capture-url-btn"
              >
                Copy
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="w-full h-14 text-lg bg-[#A94442] hover:bg-[#8B3836] rounded-full"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <div className="spinner mr-2" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
